/**
 * utils/companyProfile.ts — FRONTEND MIRROR of supabase/functions/server/companyProfileCore.ts.
 *
 * Generated: do not edit by hand; run scripts/sync-discovery-grid-mirror.sh.
 * tests/server/companyProfileCore_test.ts asserts parity.
 */


export const INDUSTRIES: readonly { value: string; label: string }[] = [
  { value: "technology", label: "Technology / SaaS" },
  { value: "healthcare", label: "Healthcare" },
  { value: "financial", label: "Financial Services" },
  { value: "retail", label: "Retail / E-commerce" },
  { value: "manufacturing", label: "Manufacturing" },
  { value: "education", label: "Education" },
  { value: "government", label: "Government" },
  { value: "consulting", label: "Consulting / Professional Services" },
  { value: "other", label: "Other" },
];

export const COMPANY_SIZES: readonly { value: string; label: string }[] = [
  { value: "1-10", label: "1-10 employees" },
  { value: "11-50", label: "11-50 employees" },
  { value: "51-200", label: "51-200 employees" },
  { value: "201-500", label: "201-500 employees" },
  { value: "501-1000", label: "501-1000 employees" },
  { value: "1000+", label: "1000+ employees" },
];

export const COMMON_ATLASSIAN_APPS: readonly string[] = [
  "Jira Software",
  "Jira Service Management",
  "Confluence",
  "Jira Work Management",
  "Bitbucket",
  "Jira Align",
  "Trello",
  "Atlas",
  "Compass",
];

export type AppType = "atlassian" | "marketplace";
export interface CompanyApp {
  id: string;
  name: string;
  type: AppType;
  licenseCount: number;
}

export const MAX_APPS = 100;
const MAX_TEXT = { name: 200, website: 300, description: 2000, appName: 120, reason: 500 };

const has = (o: unknown, k: string) => !!o && typeof o === "object" && Object.prototype.hasOwnProperty.call(o, k);
const oneOf = (list: readonly { value: string }[], v: string) => list.some((x) => x.value === v);

/** The apps and licensing inventory, checked: names, a type, whole license counts of 0 or more. */
export function normalizeApps(input: unknown): { ok: true; apps: CompanyApp[] } | { ok: false; error: string } {
  if (!Array.isArray(input)) return { ok: false, error: "apps must be a list" };
  if (input.length > MAX_APPS) return { ok: false, error: `At most ${MAX_APPS} apps` };
  const apps: CompanyApp[] = [];
  const seen = new Set<string>();
  for (const raw of input) {
    const name = typeof raw?.name === "string" ? raw.name.trim() : "";
    if (!name) return { ok: false, error: "Every app needs a name" };
    if (name.length > MAX_TEXT.appName) return { ok: false, error: `App names are at most ${MAX_TEXT.appName} characters` };
    const type: AppType = raw?.type === "marketplace" ? "marketplace" : raw?.type === "atlassian" ? "atlassian" : ("" as AppType);
    if (!type) return { ok: false, error: `"${name}" needs a type: Atlassian or Marketplace` };
    const count = raw?.licenseCount === undefined || raw?.licenseCount === "" ? 0 : Number(raw.licenseCount);
    if (!Number.isInteger(count) || count < 0) return { ok: false, error: `"${name}" needs a whole license count of 0 or more` };
    let id = typeof raw?.id === "string" && raw.id ? raw.id : `app_${apps.length}_${name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
    while (seen.has(id)) id = `${id}_`;
    seen.add(id);
    apps.push({ id, name, type, licenseCount: count });
  }
  return { ok: true, apps };
}

export interface ProfileEditor {
  /** A member of the consulting firm that owns the company, or a system admin. */
  firmSide: boolean;
}

/**
 * Checks a profile edit and returns the fields to write. Only the fields sent
 * change; an empty string clears a text field (the old route could not clear
 * one). The owner is the firm's to set; client admins edit everything else.
 * `ownerIds` are the people who may own the company (members of its firm).
 */
export function validateProfileUpdate(
  updates: any,
  editor: ProfileEditor,
  ownerIds: readonly string[],
): { ok: true; patch: Record<string, unknown> } | { ok: false; status: 400 | 403; error: string } {
  if (!updates || typeof updates !== "object" || Array.isArray(updates)) return { ok: false, status: 400, error: "Send the fields to change" };
  const patch: Record<string, unknown> = {};

  const text = (key: string, max: number) => {
    if (!has(updates, key)) return null;
    const v = updates[key];
    if (v !== null && typeof v !== "string") return `${key} must be text`;
    const t = (v ?? "").trim();
    if (t.length > max) return `${key} is at most ${max} characters`;
    patch[key] = t;
    return null;
  };

  if (has(updates, "name")) {
    const name = typeof updates.name === "string" ? updates.name.trim() : "";
    if (!name) return { ok: false, status: 400, error: "Company name is required" };
    if (name.length > MAX_TEXT.name) return { ok: false, status: 400, error: `Company name is at most ${MAX_TEXT.name} characters` };
    patch.name = name;
  }
  for (const [key, max] of [["description", MAX_TEXT.description], ["website", MAX_TEXT.website]] as const) {
    const err = text(key, max);
    if (err) return { ok: false, status: 400, error: err };
  }
  if (typeof patch.website === "string" && patch.website) {
    const w = patch.website as string;
    const url = /^https?:\/\//i.test(w) ? w : `https://${w}`;
    if (!/^https?:\/\/[^\s/]+\.[^\s/]+/i.test(url)) return { ok: false, status: 400, error: "Website must be a web address, like https://example.com" };
    patch.website = url;
  }
  for (const [key, list, label] of [
    ["industry", INDUSTRIES, "industry"],
    ["companySize", COMPANY_SIZES, "company size"],
  ] as const) {
    if (!has(updates, key)) continue;
    const v = updates[key] ?? "";
    if (v !== "" && (typeof v !== "string" || !oneOf(list, v))) return { ok: false, status: 400, error: `Unknown ${label} "${v}"` };
    patch[key] = v;
  }
  if (has(updates, "ownerId")) {
    if (!editor.firmSide) return { ok: false, status: 403, error: "The consulting firm assigns the company's owner" };
    const owner = updates.ownerId ?? "";
    if (owner !== "" && (typeof owner !== "string" || !ownerIds.includes(owner))) {
      return { ok: false, status: 400, error: "The owner must be a member of the consulting firm" };
    }
    patch.ownerId = owner || null;
  }
  if (has(updates, "apps")) {
    const apps = normalizeApps(updates.apps);
    if (!apps.ok) return { ok: false, status: 400, error: apps.error };
    patch.apps = apps.apps;
  }
  if (Object.keys(patch).length === 0) return { ok: false, status: 400, error: "Nothing to change" };
  return { ok: true, patch };
}

/** The profile the screens read, with the owner and creator resolved to names. */
export function publicCompanyProfile(company: any, people: Record<string, { name?: string; email?: string }> = {}) {
  const nameOf = (id: unknown) => (typeof id === "string" && people[id] ? people[id].name || people[id].email || null : null);
  return {
    id: company?.id ?? null,
    name: company?.name ?? "",
    website: company?.website ?? "",
    industry: company?.industry ?? "",
    companySize: company?.companySize ?? "",
    description: company?.description ?? "",
    ownerId: company?.ownerId ?? null,
    ownerName: nameOf(company?.ownerId),
    createdBy: company?.createdBy ?? null,
    createdByName: company?.createdByName ?? nameOf(company?.createdBy) ?? null,
    apps: Array.isArray(company?.apps) ? company.apps : [],
  };
}

// ---------------------------------------------------------------------------
// AI mode (on top of the company's AI switch, `aiEnabled`)
// ---------------------------------------------------------------------------

export const AI_MODES = ["full", "consultant_only"] as const;
export type AiMode = (typeof AI_MODES)[number];

export function aiModeOf(company: any): AiMode {
  return company?.aiMode === "consultant_only" ? "consultant_only" : "full";
}

/**
 * The AI fields of a preferences change. Turning AI off records when and by
 * whom, with the optional reason; turning it back on clears all three.
 */
export function aiPreferencePatch(
  current: any,
  updates: any,
  by: { id: string; name?: string | null },
  now: string,
): { ok: true; patch: Record<string, unknown> } | { ok: false; error: string } {
  const patch: Record<string, unknown> = {};
  if (has(updates, "aiMode")) {
    if (!AI_MODES.includes(updates.aiMode)) return { ok: false, error: "aiMode must be full or consultant_only" };
    patch.aiMode = updates.aiMode;
  }
  if (has(updates, "aiDisabledReason")) {
    const r = updates.aiDisabledReason ?? "";
    if (typeof r !== "string") return { ok: false, error: "aiDisabledReason must be text" };
    if (r.trim().length > MAX_TEXT.reason) return { ok: false, error: `The reason is at most ${MAX_TEXT.reason} characters` };
    patch.aiDisabledReason = r.trim() || null;
  }
  const wasOn = current?.aiEnabled !== false;
  if (updates?.aiEnabled === false && wasOn) {
    patch.aiDisabledAt = now;
    patch.aiDisabledBy = by.id;
    patch.aiDisabledByName = by.name ?? null;
  }
  if (updates?.aiEnabled === true && !wasOn) {
    patch.aiDisabledAt = null;
    patch.aiDisabledBy = null;
    patch.aiDisabledByName = null;
    patch.aiDisabledReason = null;
  }
  return { ok: true, patch };
}
